import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import { ChevronLeft, ChevronRight, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const MONTHS = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const MONTHS_FULL = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const COLORS = ["#2D4A3E", "#8AA399", "#C9A66B", "#5C7066"];

function StatCard({ label, value, testid, children }) {
  return (
    <div className="card-soft p-5" data-testid={testid}>
      <div className="overline">{label}</div>
      <div className="font-display text-3xl text-[#1A231F] mt-2">{value}</div>
      {children}
    </div>
  );
}

function Delta({ cur, prev }) {
  if (prev === 0 && cur === 0) return <div className="text-xs text-[#5C7066] mt-1 flex items-center gap-1"><Minus size={12}/> come il mese prima</div>;
  if (prev === 0) return <div className="text-xs text-[#2D4A3E] mt-1 flex items-center gap-1"><TrendingUp size={12}/> +{cur} rispetto al mese prima</div>;
  const pct = Math.round(((cur - prev) / prev) * 100);
  const Icon = pct > 0 ? TrendingUp : pct < 0 ? TrendingDown : Minus;
  const color = pct > 0 ? "text-[#2D4A3E]" : pct < 0 ? "text-[#B85C5C]" : "text-[#5C7066]";
  return <div className={`text-xs mt-1 flex items-center gap-1 ${color}`}><Icon size={12}/> {pct > 0 ? "+" : ""}{pct}% rispetto al mese prima ({prev})</div>;
}

export default function StatsPanel() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/stats", { params: { year } })
      .then(r => setData(r.data))
      .catch(e => toast.error(formatApiError(e)));
  }, [year]);

  if (!data) return <div className="text-sm text-[#5C7066]" data-testid="stats-loading">Caricamento…</div>;

  const chart = data.months.map(m => ({ name: MONTHS[m.month - 1], ...Object.fromEntries(data.studios.map(s => [s.name, m.by_studio[s.id] || 0])) }));
  const cur = data.months[month];
  const prev = month > 0 ? data.months[month - 1] : null;
  const yearTotal = data.months.reduce((t, m) => t + m.total, 0);

  return (
    <div className="space-y-6" data-testid="stats-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="inline-flex items-center gap-2 rounded-xl border border-[#E0E5E2] bg-white p-1">
          <button data-testid="stats-year-prev" onClick={() => setYear(year - 1)} className="p-2 rounded-lg hover:bg-[#2D4A3E]/10 text-[#2D4A3E]"><ChevronLeft size={16}/></button>
          <span data-testid="stats-year" className="font-display text-[#1A231F] px-2">{year}</span>
          <button data-testid="stats-year-next" onClick={() => setYear(year + 1)} className="p-2 rounded-lg hover:bg-[#2D4A3E]/10 text-[#2D4A3E]"><ChevronRight size={16}/></button>
        </div>
        <div className="text-sm text-[#5C7066]" data-testid="stats-year-total">Totale anno: <b className="text-[#1A231F]">{yearTotal}</b> prenotazioni</div>
      </div>

      <div className="card-soft p-4 sm:p-6">
        <h3 className="font-display text-lg text-[#1A231F] mb-4">Prenotazioni per mese e studio</h3>
        <div className="h-72" data-testid="stats-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart} margin={{ left: -20, right: 4 }} onClick={(e) => e?.activeTooltipIndex != null && setMonth(Number(e.activeTooltipIndex))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E0E5E2" vertical={false}/>
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#5C7066" }} interval={0}/>
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#5C7066" }}/>
              <Tooltip cursor={{ fill: "#2D4A3E10" }} contentStyle={{ borderRadius: 12, border: "1px solid #E0E5E2" }}/>
              <Legend wrapperStyle={{ fontSize: 12 }}/>
              {data.studios.map((s, i) => <Bar key={s.id} dataKey={s.name} stackId="a" fill={COLORS[i % COLORS.length]} radius={i === data.studios.length - 1 ? [6, 6, 0, 0] : 0}/>)}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div>
        <div className="flex items-center gap-3 mb-3">
          <h3 className="font-display text-lg text-[#1A231F]">Riepilogo di</h3>
          <select data-testid="stats-month-select" value={month} onChange={(e) => setMonth(Number(e.target.value))} className="h-10 rounded-xl border border-[#E0E5E2] bg-white px-3 text-sm">
            {MONTHS_FULL.map((m, i) => <option key={m} value={i}>{m} {year}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Visite" value={cur.total} testid="stats-month-total">
            {prev && <Delta cur={cur.total} prev={prev.total}/>}
          </StatCard>
          <StatCard label="Nuovi pazienti" value={cur.new_patients} testid="stats-month-new">
            {prev && <Delta cur={cur.new_patients} prev={prev.new_patients}/>}
          </StatCard>
          {data.studios.map(s => (
            <StatCard key={s.id} label={s.name} value={cur.by_studio[s.id] || 0} testid={`stats-month-studio-${s.id}`}>
              {prev && <Delta cur={cur.by_studio[s.id] || 0} prev={prev.by_studio[s.id] || 0}/>}
            </StatCard>
          ))}
        </div>
      </div>
    </div>
  );
}
